import { Box, Card, Divider, Stack, Typography, Chip } from "@mui/material";
import { Inventory } from "@mui/icons-material";
import { useTranslation } from "../../../../hooks/useTranslation";
import { CopiableChip } from "../../../../components/ui/CopiableChip";
import { SectionHeader } from "../../../../components/ui/SectionHeader";
import { SupplyOrder } from "../../types";

type Props = {
  supplyOrder: SupplyOrder;
};

export const SupplyOrderRow = ({ supplyOrder }: Props) => {
  const { translate } = useTranslation("pages.orderDetails.supplyOrders");

  const formatDate = (value?: string) => {
    if (!value) return "";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const expectedDeliveryStart =
    supplyOrder.expectedDeliveryDateStart ??
    supplyOrder.expectedDeliveryStartDate;
  const expectedDeliveryEnd =
    supplyOrder.expectedDeliveryDateEnd ?? supplyOrder.expectedDeliveryEndDate;

  return (
    <Card
      elevation={0}
      sx={{
        p: 2.5,
        backgroundColor: (theme) => theme.palette.customBackground.box,
      }}
    >
      <SectionHeader
        icon={<Inventory sx={{ fontSize: 20, color: "secondary.main" }} />}
        title={`${translate("title")} ${supplyOrder.id}`}
        color="secondary"
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              color="secondary"
              sx={{ fontWeight: 500, color: "white" }}
              label={translate(`status.${supplyOrder.status}`, {
                defaultValue: supplyOrder.status,
              })}
            />
            <CopiableChip initialLabel={supplyOrder.amazonSupplyOrderId} />
          </Stack>
        }
      />

      <Divider sx={{ my: 2 }} />

      <Stack spacing={1.25} sx={{ mb: 2 }}>
        {supplyOrder.trackingId && (
          <Box display="flex">
            <Box width={120}>
              <Typography variant="body2" fontWeight={500}>
                {translate("trackingId")}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 400 }}
            >
              {supplyOrder.trackingId}
            </Typography>
          </Box>
        )}

        {supplyOrder.carrierName && (
          <Box display="flex">
            <Box width={120}>
              <Typography variant="body2" fontWeight={500}>
                {translate("carrierName")}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 400 }}
            >
              {supplyOrder.carrierName}
            </Typography>
          </Box>
        )}

        {expectedDeliveryStart && (
          <Box display="flex">
            <Box width={120}>
              <Typography variant="body2" fontWeight={500}>
                {translate("expectedDeliveryDateStart")}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 400 }}
            >
              {formatDate(expectedDeliveryStart)}
            </Typography>
          </Box>
        )}

        {expectedDeliveryEnd && (
          <Box display="flex">
            <Box width={120}>
              <Typography variant="body2" fontWeight={500}>
                {translate("expectedDeliveryDateEnd")}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 400 }}
            >
              {formatDate(expectedDeliveryEnd)}
            </Typography>
          </Box>
        )}

        {supplyOrder.shipDate && (
          <Box display="flex">
            <Box width={120}>
              <Typography variant="body2" fontWeight={500}>
                {translate("shipDate")}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 400 }}
            >
              {formatDate(supplyOrder.shipDate)}
            </Typography>
          </Box>
        )}

        {supplyOrder.deliveryDate && (
          <Box display="flex">
            <Box width={120}>
              <Typography variant="body2" fontWeight={500}>
                {translate("deliveryDate")}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 400 }}
            >
              {formatDate(supplyOrder.deliveryDate)}
            </Typography>
          </Box>
        )}
      </Stack>

      <Stack spacing={1}>
        {supplyOrder.supplyOrderItems.map((item) => (
          <Box
            key={item.id}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 1,
              py: 0.75,
              borderRadius: 1,
              backgroundColor: "action.hover",
            }}
          >
            <CopiableChip initialLabel={item.productCode.toUpperCase()} />

            <Typography variant="body2">
              {translate("quantity")}: {item.quantity}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Card>
  );
};
